import React, { useCallback, useEffect, useState } from 'react';
import { useToast } from '../../components/UI/ToastProvider';
import {
  operationalIndicatorsService,
  OperationalIndicatorCatalogItem,
} from '../../services/operationalIndicatorsService';

const formatTarget = (item: OperationalIndicatorCatalogItem): string => {
  if (item.chartKind === 'distribution' || item.targetValue == null)
  {
    return '—';
  }

  const comparator =
    item.comparator === 'lt' ? '<' : item.comparator === 'gt' ? '>' : '';

  if (item.unit === 'percent')
  {
    return `${comparator} ${item.targetValue}%`;
  }

  if (item.unit === 'seconds')
  {
    const secondary =
      item.secondaryLimitValue != null
        ? ` (máx. ${item.secondaryLimitValue}s)`
        : '';
    return `${comparator} ${item.targetValue}s${secondary}`;
  }

  if (item.unit === 'minutes')
  {
    const secondary =
      item.secondaryLimitValue != null
        ? ` (janela ${item.secondaryLimitValue} min)`
        : '';
    return `${comparator} ${item.targetValue} min${secondary}`;
  }

  return `${comparator} ${item.targetValue}`;
};

const unitLabel = (unit: string): string => {
  switch (unit)
  {
    case 'seconds':
      return 'Segundos';
    case 'minutes':
      return 'Minutos';
    case 'percent':
      return 'Percentual';
    case 'distribution':
      return 'Distribuição';
    default:
      return unit;
  }
};

export const OperationalIndicatorsPage: React.FC = () => {
  const { addToast } = useToast();
  const [items, setItems] = useState<OperationalIndicatorCatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try
    {
      const data = await operationalIndicatorsService.listCatalog();
      setItems(data);
    }
    catch
    {
      addToast('Erro ao carregar indicadores', 'error');
    }
    finally
    {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = items.filter((item) => {
    const term = search.trim().toLowerCase();
    if (!term)
    {
      return true;
    }

    return (
      item.name.toLowerCase().includes(term)
      || item.code.toLowerCase().includes(term)
      || item.description.toLowerCase().includes(term)
    );
  });

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Indicadores</h1>
        <p className="text-gray-600 text-sm mt-1">
          Catálogo de indicadores operacionais do POP de Atendimento.
        </p>
      </div>

      <div className="mb-4">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar indicadores..."
          className="w-full max-w-md rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-500">
          Nenhum indicador encontrado.
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Nome
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Unidade
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Meta
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Tipo
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3 align-top">
                    <div className="flex items-start gap-2">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{item.name}</p>
                        <p className="mt-0.5 text-xs text-gray-500">{item.description}</p>
                        <p className="mt-1 font-mono text-[11px] text-gray-400">{item.code}</p>
                      </div>
                      {item.isSystem && (
                        <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                          Sistema
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 align-top text-sm text-gray-700">
                    {unitLabel(item.unit)}
                  </td>
                  <td className="px-4 py-3 align-top text-sm text-gray-700">
                    {formatTarget(item)}
                  </td>
                  <td className="px-4 py-3 align-top text-sm text-gray-700">
                    {item.chartKind === 'distribution' ? 'Distribuição' : 'Número'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
